import React, { useCallback, useEffect, useState } from "react";
import { Download, Search, ShieldCheck, X } from "lucide-react";
import { toast } from "sonner";
import api, { downloadFile, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { AttestationReport } from "./AdminAttestations";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const PAGE_SIZE = 25;
const OUTCOMES = ["success", "failure", "denied"];

const outcomeStyle = (outcome) => {
  if (outcome === "success") return { background: "#E7F1EA", color: "#155E3F" };
  if (outcome === "failure") return { background: "#FBE9E7", color: "#9B2C1C" };
  return { background: "#FFF4E2", color: "#8A5200" };
};

export default function AuditPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const canSeeAttestations = (user?.permissions || []).includes("audit");
  const [stream, setStream] = useState(isAdmin ? "all" : "customer");
  const [q, setQ] = useState("");
  const [action, setAction] = useState("all");
  const [outcome, setOutcome] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [events, setEvents] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);

  const filterParams = useCallback(() => ({
    q: q || undefined,
    action: action === "all" ? undefined : action,
    outcome: outcome === "all" ? undefined : outcome,
    date_from: dateFrom || undefined,
    date_to: dateTo || undefined,
  }), [q, action, outcome, dateFrom, dateTo]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/audit/${stream}`, {
        params: { ...filterParams(), limit: PAGE_SIZE, skip: page * PAGE_SIZE },
      });
      setEvents(data.events || []);
      setTotal(data.total || 0);
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [stream, page, filterParams]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get("/audit/actions")
      .then(({ data }) => setActions(data.actions || []))
      .catch(() => setActions([]));
  }, []);

  useEffect(() => { setPage(0); }, [stream, q, action, outcome, dateFrom, dateTo]);

  const exportCsv = async () => {
    try {
      await downloadFile(`/audit/${stream}/export`, "audit-log.csv", filterParams());
      toast.success("Audit CSV downloaded");
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    }
  };

  const resetFilters = () => {
    setQ(""); setAction("all"); setOutcome("all"); setDateFrom(""); setDateTo("");
  };

  const lastPage = Math.max(0, Math.ceil(total / PAGE_SIZE) - 1);

  return (
    <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-10" data-testid="audit-page">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
        <div>
          <div className="eyebrow mb-2 flex items-center gap-2"><ShieldCheck size={12}/> Security</div>
          <h1 className="font-heading font-black text-4xl sm:text-5xl leading-none tracking-tight" style={{ color: "var(--ink)" }}>
            Audit trail
          </h1>
          <p className="mt-3 text-base max-w-2xl" style={{ color: "var(--muted)" }}>
            Every sign-in, invoice decision, account change and data upload, hash-chained in order.
          </p>
        </div>
        <button onClick={exportCsv} className="btn-primary flex items-center gap-2 self-start" data-testid="audit-export-csv">
          <Download size={14}/> Export CSV
        </button>
      </div>

      {canSeeAttestations && (
        <div className="mb-10 border-b pb-10" style={{ borderColor: "var(--border)" }} data-testid="audit-attestation-section">
          <AttestationReport compact />
        </div>
      )}

      <div className="border p-6 space-y-5" style={{ borderColor: "var(--border)", background: "var(--surface)" }}>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
          <div className="lg:col-span-4">
            <label className="field-label">Search</label>
            <div className="relative">
              <Search size={14} className="absolute left-0 top-3.5" style={{ color: "var(--muted)" }}/>
              <input
                className="input-flat !pl-6"
                placeholder="Actor, action, company, invoice, IP…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                data-testid="audit-search"
              />
            </div>
          </div>
          {isAdmin && (
            <div className="lg:col-span-2">
              <label className="field-label">Stream</label>
              <Select value={stream} onValueChange={setStream}>
                <SelectTrigger className="!rounded-none !border-x-0 !border-t-0 !bg-transparent !px-0" data-testid="audit-stream"><SelectValue/></SelectTrigger>
                <SelectContent className="!rounded-none">
                  <SelectItem value="all">All activity</SelectItem>
                  <SelectItem value="admin">Admin actions</SelectItem>
                  <SelectItem value="customer">Client actions</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="lg:col-span-3">
            <label className="field-label">Action</label>
            <Select value={action} onValueChange={setAction}>
              <SelectTrigger className="!rounded-none !border-x-0 !border-t-0 !bg-transparent !px-0" data-testid="audit-action"><SelectValue/></SelectTrigger>
              <SelectContent className="!rounded-none max-h-72">
                <SelectItem value="all">All actions</SelectItem>
                {actions.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="lg:col-span-1">
            <label className="field-label">Outcome</label>
            <Select value={outcome} onValueChange={setOutcome}>
              <SelectTrigger className="!rounded-none !border-x-0 !border-t-0 !bg-transparent !px-0" data-testid="audit-outcome"><SelectValue/></SelectTrigger>
              <SelectContent className="!rounded-none">
                <SelectItem value="all">Any</SelectItem>
                {OUTCOMES.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="lg:col-span-1">
            <label className="field-label">From</label>
            <input type="date" className="input-flat" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="audit-date-from"/>
          </div>
          <div className="lg:col-span-1">
            <label className="field-label">To</label>
            <input type="date" className="input-flat" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="audit-date-to"/>
          </div>
        </div>
        <div className="flex items-center justify-between text-sm">
          <span style={{ color: "var(--muted)" }} data-testid="audit-total">{total} matching event{total === 1 ? "" : "s"}</span>
          <button onClick={resetFilters} className="btn-ghost flex items-center gap-1.5 text-xs" data-testid="audit-reset">
            <X size={12}/> Clear Filters
          </button>
        </div>
      </div>

      <div className="mt-6 border max-h-[560px] overflow-auto" style={{ borderColor: "var(--border)", background: "var(--surface)" }} data-testid="audit-list-region">
        <table className="w-full text-sm min-w-[900px]">
          <thead>
            <tr className="border-b eyebrow text-left" style={{ borderColor: "var(--border)" }}>
              <th className="px-4 py-3">When (UTC)</th>
              <th className="px-4 py-3">Actor</th>
              <th className="px-4 py-3">Action</th>
              <th className="px-4 py-3">Detail</th>
              <th className="px-4 py-3">Company</th>
              <th className="px-4 py-3">IP</th>
              <th className="px-4 py-3">Outcome</th>
            </tr>
          </thead>
          <tbody data-testid="audit-rows">
            {events.map((event) => (
              <tr key={event.event_id} className="border-b align-top" style={{ borderColor: "var(--border)" }} data-testid={`audit-row-${event.event_id}`}>
                <td className="px-4 py-3 font-mono text-xs whitespace-nowrap" style={{ color: "var(--muted)" }}>
                  {new Date(event.occurred_at).toISOString().slice(0, 19).replace("T", " ")}
                </td>
                <td className="px-4 py-3">
                  <div className="font-medium" style={{ color: "var(--ink)" }}>{event.actor_name}</div>
                  <div className="text-xs" style={{ color: "var(--muted)" }}>{event.actor_type}</div>
                </td>
                <td className="px-4 py-3 font-mono text-xs" style={{ color: "var(--ink)" }}>{event.action}</td>
                <td className="px-4 py-3" style={{ color: "var(--muted)" }}>{event.summary}</td>
                <td className="px-4 py-3" style={{ color: "var(--muted)" }}>{event.company || "—"}</td>
                <td className="px-4 py-3 font-mono text-xs" style={{ color: "var(--muted)" }}>{event.source_ip || "—"}</td>
                <td className="px-4 py-3">
                  <span className="px-2 py-1 text-[10px] font-bold uppercase tracking-wider" style={outcomeStyle(event.outcome)}>
                    {event.outcome}
                  </span>
                </td>
              </tr>
            ))}
            {!events.length && (
              <tr><td colSpan={7} className="p-12 text-center text-sm" style={{ color: "var(--muted)" }} data-testid="audit-empty">
                {loading ? "Loading audit events…" : "No audit events match these filters."}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-5 flex items-center justify-between">
        <span className="text-xs" style={{ color: "var(--muted)" }} data-testid="audit-page-label">
          Page {page + 1} of {lastPage + 1}
        </span>
        <div className="flex gap-2">
          <button className="btn-ghost text-xs" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))} data-testid="audit-prev">
            Previous
          </button>
          <button className="btn-ghost text-xs" disabled={page >= lastPage} onClick={() => setPage((p) => Math.min(lastPage, p + 1))} data-testid="audit-next">
            Next
          </button>
        </div>
      </div>
    </div>
  );
}
